import { DateLike } from '@topconagriculture/language';

import { Forms } from '../../form/form.selector';

export type DriverChangesSettings = {
  mixerChangesDefaultSaveCW: boolean;
  mixerChangesDefaultSaveDM: boolean;
  mixerChangesDefaultSaveHC: boolean;
  mixerChangesCWSaveMode: MixerChangesCWSaveMode;
};

export type LoadOptimizationSettings = {
  minimumSplitInclude: number;
  minimumSplitSave: number;
  optimizeLoadsMode: number;
  feedingSendMode: FeedingSendMode;
};

export enum ScaleWeightUnitType {
  KG = 1,
  LBS = 2,
}

export type OtherSettings = {
  scaleWeightUnit: ScaleWeightUnitType;
  currency: string;
  rationEntryMethod: number;
  useZoneControl: boolean;
  premixAmountsByPercentage: boolean;
  refusalSubtractModeDefault: RefusalSubtractModeDefault;
  useWeightMotionTolerance: boolean;
  timeShift: number;
  minimumLoadWeight: number;
  timeZone: string;
  useLockedFeedings: boolean;
};

export type AgentSettings = {
  hmsHCSystem: string;
  hmsHCImport: boolean;
  hmsMPSystem: string;
  hmsMPImport: boolean;
  hmsECSystem: string;
  hmsECImport: boolean;
  hmsAutoRead: boolean;
  hmsAgentActive: boolean;
};

export type DatalinkSettings = {
  dlType: DlType;
  dlExtFeedLineType: DlExtFeedLineType;
  dlAutoSend: boolean;
  communicationInterval: number;
  communicationStartTime: string;
  communicationEndTime: string;
  dlExtFilePath: string;
  dlAutoReceiveMode: number;
  dlRadioComPort: number;
  dlRadioReserveChannel: number;
  dlRadioRadioType: DlRadioRadioType;
  dlLineByLine: boolean;
  dlIgnoreUnfinishedLoads: boolean;
  dlSupportOldIndicators: boolean;
  useMixerRoles: boolean;
  dlRadioReadsPerSecond: number;
  dlRadioDeletesPerSecond: number;
  dlRadioWaitSeconds: number;
  dlWifiConnectTimeout: number;
  dlWifiRetryTime: number;
};

export interface InventorySettingsType {
  automateShrTime: string;
  automateShrWeekday: number;
  automateShrMonthday: number;
}

export interface ThirdParty {
  id: string;
  name: string;
  shortName: string;
  clientNo: number;
  active: boolean;
}

export interface WeighbridgeSystem {
  name: string;
  code: number;
}

export interface WeighbridgeSettingsType {
  weighbridgeAutoRead: boolean;
  weighbridgeSystem: string;
  weighBridgeSystemsList?: WeighbridgeSystem[];
}

export interface BunkReaderScoreSettings {
  bunkScoreGrades: BunkScoreGrade[] | null;
  brAdjustmentType: BunkReaderAdjustmentType;
  brAutoProcess: boolean;
  brDefaultscore: number;
  brExpireTimeInHours: number;
}

export interface ExternalFeedingSystemsSettings {
  fsAdvancedSettings: boolean;
  fsMineralsInGrams: boolean;
  fsAutoReceive: boolean;
  fsAutoSend: boolean;
  fsInputFolder: string;
  fsInputFilename: string;
  fsOutputFolder: string;
  fsOutputFilename: string;
  fsSendStartTime: string;
  communicationInterval: number;
  fsUseAgent: boolean;
}

export interface OperatorManagementSettings {
  driverUsePermissions: boolean;
  driverRequirePIN: boolean;
  driverAutoLockScreen: boolean;
  driverAutoLockScreenDelay: number;
}

export type SettingsManage = {
  driverChanges: DriverChangesSettings;
  loadOptimization: LoadOptimizationSettings;
  otherSettings: OtherSettings;
  agentSettings: AgentSettings;
  dataLinkSettings: DatalinkSettings;
  inventorySettings: InventorySettingsType;
  weighbridgeSettings: WeighbridgeSettingsType;
  bunkReaderScoreSettings: Partial<BunkReaderScoreSettings>;
  thirdParties: ThirdParty[];
  externalFeedingSystemsSettings: ExternalFeedingSystemsSettings;
  defaultDensity: number;
  dlCommunicationStarted: boolean;
  operatorManagementSettings: OperatorManagementSettings;
  unlimitedFeedings: boolean;
};

export enum SettingsType {
  driverChanges = 'driverChanges',
  loadsProgramming = 'loadsProgramming',
  feedingPeriods = 'feedingPeriods',
  herdManagementSystem = 'herdManagementSystem',
  penTypes = 'penTypes',
  lotMarketTypes = 'lotMarketTypes',
  lotTypes = 'lotTypes',
  dataLinkSettings = 'dataLinkSettings',
  feedSettingsInventory = 'feedSettingsInventory',
  weighbridgeSystem = 'weighbridgeSystem',
  otherSettings = 'otherSettings',
  thirdPartyIntegration = 'thirdPartyIntegration',
  costTypes = 'costTypes',
  bunkReaderPenOrder = 'bunkReaderPenOrder',
  bunkReaderScoreSettings = 'bunkReaderScoreSettings',
  externalFedingFeedingSystems = 'externalFedingFeedingSystems',
  operatorManagement = 'operatorManagement',
  locations = 'locations',
  binControl = 'binControl',
}

export const SettingForms = [
  Forms.settingsForm,
  Forms.driverChanges,
  Forms.agentSettings,
  Forms.loadsProgramming,
  Forms.datalinkSettings,
  Forms.otherSettings,
  Forms.feedingPeriod,
  Forms.weighBridgeSystem,
  Forms.thirdPartyIntegration,
  Forms.externalFeedingSystem,
  Forms.operatorsManagement,
  Forms.operatorsPermissions,
];

export type Params = Partial<DriverChangesSettings & LoadOptimizationSettings & OtherSettings & AgentSettings>;

export type BunkScoreGrade = {
  id: string;
  score: number;
  percentage: number;
  weight: number;
  notInUse: boolean;
};

export enum BunkScoresStatus {
  SUGGESTED = 'suggested',
  SUGGESTION_EXPIRED = 'suggestion_expired',
  SUGGESTION_CONFIRMED = 'suggestion_confirmed',
  SUGGESTION_CONFIRMED_AUTOMATICLY = 'suggestion_confirmed_automaticly',
  PROCESSED = 'processed',
  PROCESSED_MANUAL_ENTRY = 'processed_manual_entry',
}

export interface BunkScoresPayload {
  userId: string;
  timestamp: DateLike;
  status: BunkScoresStatus;
  gradeId?: number;
  resultPercentage?: number;
  resultWeight?: number;
}

export interface BunkScoresResponse {
  groupId: string;
  timestamp: DateLike;
}

export enum BunkReaderAdjustmentType {
  DAILY_REQUIREMENT = 0,
  ACTUAL_WEIGHT = 1,
}

export enum DlRadioRadioType {
  STANDARD_RADIO_TYPE = 0,
  EXTENDED_RADIO_TYPE = 1,
}

export enum DlType {
  WIFI = 1,
  RADIO = 2,
  EXTERNAL_DATALINK = 3,
}

export enum DlExtFeedLineType {
  LEGACY = 0,
  EXPANDED = 1,
}

export enum FeedingSendMode {
  LOADS_MODE = 1,
  LIST_MODE = 2,
}

export enum MixerChangesCWSaveMode {
  DAILY_REQUIREMENT = 0,
  FEEDING_FACTOR = 1,
}

export enum RefusalSubtractModeDefault {
  PREVIOUS_DELIVERY = 1,
  TWO_DELIVERIES_AGO = 2,
  PREVIOUS_DAY = 3,
}

export type FarmParams = {
  brAdjustmentType: BunkReaderAdjustmentType;
  brAutoProcess: boolean;
  brDefaultscore: number;
  brExpireTimeInHours: number;
  communicationEndTime: string;
  communicationInterval: number;
  communicationStartTime: string;
  currency: string;
  dlAutoReceiveMode: number; // @todo: enum
  dlAutoSend: boolean;
  dlCommunicationStarted: boolean;
  dlExtFeedLineType: DlExtFeedLineType;
  dlExtFilePath: string;
  dlIgnoreUnfinishedLoads: boolean;
  dlLineByLine: boolean;
  dlRadioComPort: number;
  dlRadioDeletesPerSecond: number;
  dlRadioRadioType: DlRadioRadioType;
  dlRadioReadsPerSecond: number;
  dlRadioReserveChannel: number;
  dlRadioWaitSeconds: number;
  dlSupportOldIndicators: boolean;
  dlType: DlType;
  dlWifiConnectTimeout: number;
  dlWifiRetryTime: number;
  feedingSendMode: FeedingSendMode;
  hmsAgentActive: boolean;
  minimumLoadWeight: number;
  minimumSplitInclude: number;
  minimumSplitSave: number;
  mixerChangesCWSaveMode: MixerChangesCWSaveMode;
  mixerChangesDefaultSaveCW: boolean;
  mixerChangesDefaultSaveDM: boolean;
  mixerChangesDefaultSaveHC: boolean;
  optimizeLoadsMode: number; // @todo: enum
  premixAmountsByPercentage: boolean;
  rationEntryMethod: number; // @todo: enum
  refusalSubtractModeDefault: RefusalSubtractModeDefault;
  scaleWeightUnit: ScaleWeightUnitType;
  timeShift: number;
  timeZone: string;
  useLockedFeedings: boolean;
  useMixerRoles: boolean;
  useWeightMotionTolerance: boolean;
  useZoneControl: boolean;
  currencyString: string;
  defaultDensity: number;
  // @todo: check if we need items below. it is undefined from the backend
  hmsHCSystem: string;
  hmsMPSystem: string;
  hmsECSystem: string;
  hmsAutoRead: boolean;
  hmsHCImport: boolean;
  hmsMPImport: boolean;
  hmsECImport: boolean;
  automateShrTime: string;
  automateShrWeekday: number;
  automateShrMonthday: number;
  weighbridgeAutoRead: boolean;
  weighbridgeSystem: string;
  fsAdvancedSettings: boolean;
  fsMineralsInGrams: boolean;
  fsAutoReceive: boolean;
  fsAutoSend: boolean;
  fsInputFolder: string;
  fsInputFilename: string;
  fsOutputFolder: string;
  fsOutputFilename: string;
  fsSendStartTime: string;
  fsUseAgent: boolean;
  driverUsePermissions: boolean;
  driverRequirePIN: boolean;
  driverAutoLockScreen: boolean;
  driverAutoLockScreenDelay: number;
  unlimitedFeedings: boolean;
};
